import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  GapExtended,
  Gap,
  Module,
  ModuleSummary,
  Run,
  GapItem,
  DetailedGapDescription,
  WizardConfiguration,
  Characteristic,
  DiversityVariant,
  ContextModel,
  EmpiricalConceptConfig,
  FsOFAValue,
  FsOFAOverall,
} from '../../shared/model/models'
import {
  setRuns,
  setModules,
  setModulesSummary,
  setModulesInfo,
  setContextVariantGaps,
  setContextVariantGapLatest,
  setContextVariantsInfo,
  setModuleGapsLatest,
  setDetailedGapDescription,
  setConfiguration,
  setCharacteristics,
  setDiversityVariants,
  setContextModels,
  setEmpiricalConceptConfigsForIndependentCharacteristics,
  setEmpiricalConceptConfigsForDescriptiveCharacteristics,
  setEmpiricalConceptsInfo,
  setRunsForETRLKPIs,
  setKPIsForCurrentRun,
  setOFAValues,
  setOFAOverall,
  setCWIMappings,
} from '../actions/analysis'
import { ETRLKPI } from '../../shared/model'

type AnalysisState = {
  runs: Run[] | null
  currentRun: Run | null
  modules: Module[] | null
  modulesSummary: ModuleSummary[] | null
  modulesInfo: Record<string, string>[] | null
  contextVariantGaps: Record<number, GapItem[]> | null
  contextVariantGapLatest: Record<number, GapExtended> | null
  contextVariantsInfo: Record<string, Record<string, GapExtended>> | null
  moduleGapsLatest: Gap[] | null
  detailedGapDescription: Record<string, DetailedGapDescription> | null
  configuration: WizardConfiguration | null
  allCharacteristics: Characteristic[] | null
  diversityVariants: DiversityVariant[] | null
  contextModels: ContextModel[] | null
  empiricalConceptConfigsForIndependentCharacteristics: Record<
    number,
    EmpiricalConceptConfig[]
  > | null
  empiricalConceptConfigsForDescriptiveCharacteristics: Record<
    number,
    EmpiricalConceptConfig[]
  > | null
  empiricalConceptsInfo: Record<string, Record<string, any>> | null
  runsForETRLKPIs: Run[] | null
  etrlKPIs: Record<string, ETRLKPI[]> | null
  ofaValues: Record<string, FsOFAValue[]> | null
  ofaBEValues: Record<string, FsOFAValue[]> | null
  ofaOverall: Record<string, FsOFAOverall> | null
  ofaTrainingCost: number
  cwiMappings: Record<number, Record<string, {kldb20105: string, titleKldb: string, bezeichnungKldb: string}[]>> | null
  currentFairViewTab: string
}

const initialState: AnalysisState = {
  runs: null,
  currentRun: null,
  modules: null,
  modulesSummary: null,
  modulesInfo: null,
  contextVariantGaps: null,
  contextVariantGapLatest: null,
  contextVariantsInfo: null,
  moduleGapsLatest: null,
  detailedGapDescription: null,
  configuration: null,
  allCharacteristics: null,
  diversityVariants: null,
  contextModels: null,
  empiricalConceptConfigsForIndependentCharacteristics: null,
  empiricalConceptConfigsForDescriptiveCharacteristics: null,
  empiricalConceptsInfo: null,
  runsForETRLKPIs: null,
  etrlKPIs: null,
  ofaValues: null,
  ofaBEValues: null,
  ofaOverall: null,
  ofaTrainingCost: 100000,
  cwiMappings: null,
  currentFairViewTab: '0',
}

const analysisSlice = createSlice({
  name: 'currentAnalysis',
  initialState,
  reducers: {
    setCurrentRun: (state, action: PayloadAction<Run | null>) => {
      state.currentRun = action.payload
      state.contextVariantGapLatest = null
      state.detailedGapDescription = null
      state.ofaValues = null
      state.ofaOverall = null
    },
    setOFAOverall: (
      state,
      action: PayloadAction<{
        contextModelVariantId: number
        data: FsOFAOverall
      }>,
    ) => {
      if (action.payload) {
        state.ofaOverall = {
          ...state.ofaOverall,
          [action.payload.contextModelVariantId]: action.payload.data,
        }
      }
    },
    setOFAValues: (
      state,
      action: PayloadAction<{
        contextModelVariantId: number
        data: FsOFAValue[]
      }>,
    ) => {
      if (action.payload) {
        state.ofaValues = {
          ...state.ofaValues,
          [action.payload.contextModelVariantId]: action.payload.data,
        }
      }
    },
    setOFATrainingCost: (state, action: PayloadAction<number>) => {
      state.ofaTrainingCost = action.payload
    },
    setCurrentFairViewTab: (state, action: PayloadAction<string>) => {
      state.currentFairViewTab = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setRuns.fulfilled, (state, action: PayloadAction<Run[] | null>) => {
      state.runs = action.payload
      if (action.payload && action.payload.length > 0)
        state.currentRun = action.payload[action.payload.length - 1]
    })
    builder.addCase(setModules.fulfilled, (state, action: PayloadAction<Module[] | null>) => {
      state.modules = action.payload
    })
    builder.addCase(
      setModulesSummary.fulfilled,
      (state, action: PayloadAction<ModuleSummary[] | null>) => {
        state.modulesSummary = action.payload
      },
    )
    builder.addCase(
      setModulesInfo.fulfilled,
      (state, action: PayloadAction<Record<string, string>[] | null>) => {
        state.modulesInfo = action.payload
      },
    )
    builder.addCase(
      setContextVariantGaps.fulfilled,
      (state, action: PayloadAction<{ contextVariantId: number; gaps: GapItem[] } | null>) => {
        if (action.payload) {
          state.contextVariantGaps = {
            ...state.contextVariantGaps,
            [action.payload.contextVariantId]: action.payload.gaps,
          }
        }
      },
    )
    builder.addCase(
      setContextVariantGapLatest.fulfilled,
      (
        state,
        action: PayloadAction<{
          contextVariantId: number
          latestGap: GapExtended
        } | null>,
      ) => {
        if (action.payload) {
          state.contextVariantGapLatest = {
            ...state.contextVariantGapLatest,
            [action.payload.contextVariantId]: action.payload.latestGap,
          }
        }
      },
    )
    builder.addCase(
      setContextVariantsInfo.fulfilled,
      (
        state,
        action: PayloadAction<{ moduleId: number; info: Record<string, GapExtended> } | null>,
      ) => {
        if (action.payload)
          state.contextVariantsInfo = {
            ...state.contextVariantsInfo,
            [action.payload.moduleId]: action.payload.info,
          }
      },
    )
    builder.addCase(setModuleGapsLatest.fulfilled, (state, action: PayloadAction<Gap[] | null>) => {
      state.moduleGapsLatest = action.payload
    })
    builder.addCase(
      setDetailedGapDescription.fulfilled,
      (
        state,
        action: PayloadAction<{ id: string; description: DetailedGapDescription } | null>,
      ) => {
        if (action.payload) {
          state.detailedGapDescription = {
            ...state.detailedGapDescription,
            [action.payload.id]: action.payload.description,
          }
        }
      },
    )
    builder.addCase(
      setConfiguration.fulfilled,
      (state, action: PayloadAction<WizardConfiguration | null>) => {
        state.configuration = action.payload
      },
    )
    builder.addCase(
      setCharacteristics.fulfilled,
      (state, action: PayloadAction<Characteristic[] | null>) => {
        state.allCharacteristics = action.payload
      },
    )
    builder.addCase(
      setDiversityVariants.fulfilled,
      (state, action: PayloadAction<DiversityVariant[] | null>) => {
        state.diversityVariants = action.payload
      },
    )
    builder.addCase(
      setContextModels.fulfilled,
      (state, action: PayloadAction<ContextModel[] | null>) => {
        state.contextModels = action.payload
      },
    )
    builder.addCase(
      setEmpiricalConceptConfigsForIndependentCharacteristics.fulfilled,
      (
        state,
        action: PayloadAction<{ contextModelId: number; data: EmpiricalConceptConfig[] } | null>,
      ) => {
        if (action.payload)
          state.empiricalConceptConfigsForIndependentCharacteristics = {
            ...state.empiricalConceptConfigsForIndependentCharacteristics,
            [action.payload.contextModelId]: action.payload.data,
          }
      },
    )
    builder.addCase(
      setEmpiricalConceptConfigsForDescriptiveCharacteristics.fulfilled,
      (
        state,
        action: PayloadAction<{ contextModelId: number; data: EmpiricalConceptConfig[] } | null>,
      ) => {
        if (action.payload)
          state.empiricalConceptConfigsForDescriptiveCharacteristics = {
            ...state.empiricalConceptConfigsForDescriptiveCharacteristics,
            [action.payload.contextModelId]: action.payload.data,
          }
      },
    )
    builder.addCase(
      setEmpiricalConceptsInfo.fulfilled,
      (
        state,
        action: PayloadAction<{ contextVariantId: number; info: Record<string, any> } | null>,
      ) => {
        if (action.payload) {
          state.empiricalConceptsInfo = {
            ...state.empiricalConceptsInfo,
            [action.payload.contextVariantId]: action.payload.info,
          }
        }
      },
    )
    builder.addCase(setRunsForETRLKPIs.fulfilled, (state, action: PayloadAction<Run[] | null>) => {
      state.runsForETRLKPIs = action.payload
    })
    builder.addCase(
      setKPIsForCurrentRun.fulfilled,
      (state, action: PayloadAction<{ runId: number; results: ETRLKPI[] } | null>) => {
        if (action.payload) {
          state.etrlKPIs = {
            ...state.etrlKPIs,
            [action.payload.runId]: action.payload.results,
          }
        }
      },
    )
    builder.addCase(
      setOFAValues.fulfilled,
      (
        state,
        action: PayloadAction<{
          contextModelVariantId: number
          data: FsOFAValue[]
          beData?: FsOFAValue[]
        } | null>,
      ) => {
        if (action.payload) {
          state.ofaValues = {
            ...state.ofaValues,
            [action.payload.contextModelVariantId]: action.payload.data,
          }
          if (action.payload.beData) {
            state.ofaBEValues = {
              ...state.ofaBEValues,
              [action.payload.contextModelVariantId]: action.payload.beData,
            }
          }
        }
      },
    )
    builder.addCase(
      setOFAOverall.fulfilled,
      (
        state,
        action: PayloadAction<{ contextModelVariantId: number; data: FsOFAOverall } | null>,
      ) => {
        if (action.payload) {
          state.ofaOverall = {
            ...state.ofaOverall,
            [action.payload.contextModelVariantId]: action.payload.data,
          }
        }
      },
    )
    builder.addCase(
      setCWIMappings.fulfilled,
      (state, action: PayloadAction<{ runId: number; data: any } | null>) => {
        if (action.payload) {
          state.cwiMappings = {
            ...state.cwiMappings,
            [action.payload.runId]: action.payload.data,
          }
        }
      },
    )
  },
})

export const analysisActions = analysisSlice.actions
export default analysisSlice.reducer
